// src/features/goal/services/objectiveSyncService.ts
// Objective 同步服務 - 本地優先架構的背景同步機制

import { functions } from '../../../config/firebase';
import { httpsCallable } from 'firebase/functions';
import {
  LocalObjective,
  getPendingObjectives,
  updateObjectiveSyncStatus,
  getAllObjectives,
  initDB,
  toLocalObjective,
} from '../../../services/indexedDB';
import { STORE_NAMES } from '../../../services/indexedDB/core/database';
import { FirestoreTimestampLike } from './indexedDB/objectiveRepository';
import {
  Objective,
  CreateObjectiveRequest,
  UpdateObjectiveRequest,
} from '../types/objective';
import { logger } from '../../../utils/logger';

// API 響應類型
interface CreateObjectiveResponse {
  success: boolean;
  objectiveId: string;
  message: string;
}

interface UpdateObjectiveResponse {
  success: boolean;
  message: string;
}

interface GetObjectivesResponse {
  success: boolean;
  objectives: Objective[];
  count: number;
}

// 同步結果
export interface SyncResult {
  success: number;
  failed: number;
  errors: Array<{ id: string; error: string }>;
}

// 同步狀態監聽器
type SyncStatusListener = (syncing: boolean, pendingCount: number) => void;
const syncStatusListeners: Set<SyncStatusListener> = new Set();

// 同步鎖（防止重複同步）
let isSyncing = false;

/**
 * 訂閱同步狀態變更
 */
export const subscribeSyncStatus = (listener: SyncStatusListener): (() => void) => {
  syncStatusListeners.add(listener);
  return () => syncStatusListeners.delete(listener);
};

/**
 * 通知同步狀態變更
 */
const notifySyncStatus = (syncing: boolean, pendingCount: number) => {
  syncStatusListeners.forEach(listener => listener(syncing, pendingCount));
};

/**
 * 同步單個 Objective 到伺服器
 */
const syncObjectiveToServer = async (objective: LocalObjective): Promise<string | null> => {
  const isLocalOnly = objective.isLocalOnly || objective.id.startsWith('temp_');

  try {
    if (isLocalOnly) {
      // 新建：調用 createObjective
      const createFunc = httpsCallable<CreateObjectiveRequest, CreateObjectiveResponse>(
        functions,
        'createObjective'
      );

      const request: CreateObjectiveRequest = {
        title: objective.title,
        category: objective.category,
        status: objective.status,
        linkedObjectiveId: objective.linkedObjectiveId,
        targetDate: objective.targetDate ? new Date(objective.targetDate) : undefined,
      };

      const result = await createFunc(request);

      if (result.data.success) {
        logger.info('ObjectiveSyncService: Created objective on server', {
          tempId: objective.id,
          serverId: result.data.objectiveId,
        });
        return result.data.objectiveId; // 返回伺服器 ID
      } else {
        throw new Error(result.data.message);
      }
    } else {
      // 更新：調用 updateObjective
      const updateFunc = httpsCallable<
        { objectiveId: string } & UpdateObjectiveRequest,
        UpdateObjectiveResponse
      >(functions, 'updateObjective');

      const result = await updateFunc({
        objectiveId: objective.id,
        title: objective.title,
        status: objective.status,
        targetDate: objective.targetDate ? new Date(objective.targetDate) : undefined,
      });

      if (result.data.success) {
        logger.info('ObjectiveSyncService: Updated objective on server', {
          objectiveId: objective.id,
        });
        return objective.id;
      } else {
        throw new Error(result.data.message);
      }
    }
  } catch (error) {
    logger.error('ObjectiveSyncService: Failed to sync objective', {
      objectiveId: objective.id,
      error,
    });
    throw error;
  }
};

/**
 * 處理所有待同步的 Objectives
 */
export const processPendingObjectives = async (userId: string): Promise<SyncResult> => {
  if (isSyncing) {
    logger.dev('ObjectiveSyncService: Sync already in progress, skipping');
    return { success: 0, failed: 0, errors: [] };
  }

  isSyncing = true;
  const result: SyncResult = { success: 0, failed: 0, errors: [] };

  try {
    const pendingObjectives = await getPendingObjectives(userId);

    if (pendingObjectives.length === 0) {
      logger.dev('ObjectiveSyncService: No pending objectives to sync');
      return result;
    }

    logger.info('ObjectiveSyncService: Starting sync', {
      pendingCount: pendingObjectives.length,
    });

    notifySyncStatus(true, pendingObjectives.length);

    for (const objective of pendingObjectives) {
      try {
        const serverId = await syncObjectiveToServer(objective);

        if (serverId) {
          // 更新本地狀態為已同步
          await updateObjectiveSyncStatus(objective.id, 'synced', serverId);
          result.success++;
        }
      } catch (error) {
        result.failed++;
        result.errors.push({
          id: objective.id,
          error: error instanceof Error ? error.message : 'Unknown error',
        });

        // 如果是權限或認證錯誤，不繼續嘗試
        if (error instanceof Error &&
            (error.message.includes('unauthenticated') ||
             error.message.includes('permission-denied'))) {
          logger.warn('ObjectiveSyncService: Auth error, stopping sync');
          break;
        }
      }
    }

    logger.info('ObjectiveSyncService: Sync completed', result);
  } catch (error) {
    logger.error('ObjectiveSyncService: Sync failed', error);
  } finally {
    isSyncing = false;

    // 獲取剩餘待同步數量
    const remaining = await getPendingObjectives(userId);
    notifySyncStatus(false, remaining.length);
  }

  return result;
};

/**
 * 從伺服器拉取最新的 Objectives 並更新本地快取
 * 使用完整替換策略：刪除所有已同步的本地資料，用伺服器資料替換
 * 保留待同步的本地變更不被覆蓋
 */
export const pullFromServer = async (userId: string): Promise<void> => {
  try {
    logger.dev('ObjectiveSyncService: Pulling objectives from server');

    const getObjectivesFunc = httpsCallable<{ limit?: number }, GetObjectivesResponse>(
      functions,
      'getObjectives'
    );

    const result = await getObjectivesFunc({
      limit: 50, // 獲取所有非歸檔的
    });

    if (result.data.success && result.data.objectives) {
      const serverObjectives = result.data.objectives;

      // 獲取本地待同步的 Objectives（這些不能被刪除）
      const pendingLocal = await getPendingObjectives(userId);
      const pendingIds = new Set(pendingLocal.map(obj => obj.id));

      logger.dev('ObjectiveSyncService: Replacing local data with server data', {
        serverCount: serverObjectives.length,
        pendingCount: pendingLocal.length,
      });

      // 先獲取所有本地資料（在事務外執行）
      const allLocal = await getAllObjectives(userId);
      const syncedLocal = allLocal.filter(obj => obj.syncStatus === 'synced');

      logger.dev('ObjectiveSyncService: Local data analysis', {
        totalLocal: allLocal.length,
        syncedLocal: syncedLocal.length,
        pendingLocal: pendingLocal.length,
      });

      // 使用事務進行完整替換
      const db = await initDB();
      const tx = db.transaction(STORE_NAMES.OBJECTIVES, 'readwrite');
      const store = tx.objectStore('objectives');

      let deletedCount = 0;
      let updatedCount = 0;

      // 1. 刪除所有已同步的本地資料（保留 pending 的）
      for (const obj of syncedLocal) {
        if (!pendingIds.has(obj.id)) {
          store.delete(obj.id);
          deletedCount++;
        }
      }

      // 2. 寫入伺服器資料（過濾掉待同步的 ID，避免覆蓋）
      for (const serverObj of serverObjectives) {
        if (!pendingIds.has(serverObj.id)) {
          const localObj = toLocalObjective(serverObj, 'synced');
          store.put(localObj);
          updatedCount++;
        }
      }

      // 3. 重新寫入待同步項目，確保它們不被意外刪除
      for (const pendingObj of pendingLocal) {
        store.put(pendingObj);
      }

      await new Promise((resolve, reject) => {
        tx.oncomplete = () => resolve(undefined);
        tx.onerror = () => reject(tx.error);
      });

      logger.info('ObjectiveSyncService: Replacement complete', {
        deleted: deletedCount,
        updated: updatedCount,
        pendingPreserved: pendingLocal.length,
      });

      logger.info('ObjectiveSyncService: Successfully replaced local data with server data', {
        serverCount: serverObjectives.length,
        pendingCount: pendingLocal.length,
      });
    }
  } catch (error) {
    logger.error('ObjectiveSyncService: Failed to pull from server', error);
    throw error;
  }
};

/**
 * 完整的雙向同步流程
 * 1. 先推送本地變更
 * 2. 再拉取伺服器最新資料
 */
export const fullSync = async (userId: string): Promise<SyncResult> => {
  logger.dev('ObjectiveSyncService: Starting full sync');

  // 1. 推送本地變更
  const pushResult = await processPendingObjectives(userId);

  // 2. 拉取伺服器資料
  try {
    await pullFromServer(userId);
  } catch (error) {
    // 拉取失敗不影響推送結果
    logger.warn('ObjectiveSyncService: Pull failed but push completed', error);
  }

  return pushResult;
};

/**
 * 檢測網路狀態並自動同步
 */
export const setupAutoSync = (userId: string): (() => void) => {
  let syncTimeoutId: NodeJS.Timeout | null = null;

  const handleOnline = () => {
    logger.dev('ObjectiveSyncService: Online, scheduling sync');
    // 延遲一秒再同步，避免頻繁觸發
    if (syncTimeoutId) clearTimeout(syncTimeoutId);
    syncTimeoutId = setTimeout(() => {
      fullSync(userId);
    }, 1000);
  };

  // 監聽上線事件
  window.addEventListener('online', handleOnline);

  // 如果當前在線，立即同步
  if (navigator.onLine) {
    handleOnline();
  }

  // 返回清理函數
  return () => {
    window.removeEventListener('online', handleOnline);
    if (syncTimeoutId) clearTimeout(syncTimeoutId);
  };
};

/**
 * 獲取當前同步狀態
 */
export const getSyncStatus = async (userId: string): Promise<{
  isSyncing: boolean;
  pendingCount: number;
}> => {
  const pending = await getPendingObjectives(userId);
  return {
    isSyncing,
    pendingCount: pending.length,
  };
};

/**
 * 衝突解決：使用 Last-Write-Wins 策略
 * 比較 updatedAt，較新的覆蓋較舊的
 */
export const resolveConflict = async (
  localObjective: LocalObjective,
  serverObjective: Objective
): Promise<'local' | 'server'> => {
  const localUpdatedAt = localObjective.updatedAt;

  // 轉換伺服器時間戳
  let serverUpdatedAt: number;
  if (serverObjective.updatedAt) {
    if (typeof serverObjective.updatedAt === 'number') {
      serverUpdatedAt = serverObjective.updatedAt;
    } else if ('toDate' in (serverObjective.updatedAt as FirestoreTimestampLike)) {
      serverUpdatedAt = (serverObjective.updatedAt as { toDate: () => Date }).toDate().getTime();
    } else if ('seconds' in (serverObjective.updatedAt as FirestoreTimestampLike)) {
      serverUpdatedAt = (serverObjective.updatedAt as { seconds: number }).seconds * 1000;
    } else {
      serverUpdatedAt = 0;
    }
  } else {
    serverUpdatedAt = 0;
  }

  // Last-Write-Wins
  return localUpdatedAt > serverUpdatedAt ? 'local' : 'server';
};
